import { useSyncExternalStore } from 'react'
import { readPhase, readScene, skyFor, choosePhase, chooseScene, scatterClouds, applySky, nextPhase, SCENES } from '../lib/sky.mjs'
import { track } from '../lib/analytics'

// A tiny external store for the sky. The server snapshot is null, so SSR and
// hydration render nothing that depends on the roll; the first client read
// picks up what the head script stamped on <html>.

export { SCENES }

export const PHASE_LABELS = {
  dawn: 'dawn',
  day: 'daylight',
  golden: 'golden hour',
  dusk: 'dusk',
  night: 'night',
}

const listeners = new Set()
let snapshot = null
let started = false

function refresh() {
  const root = document.documentElement
  const sky = choosePhase(window.location.search, root, readPhase, skyFor)
  sky.scene = chooseScene(window.location.search, root, readScene)
  scatterClouds(root)
  snapshot = sky
  applySky(document, sky)
  listeners.forEach((l) => l())
}

// Once settled, the sun crawls toward whichever end of its arc is farther
// away, at a pace that would cross the whole arc in ten minutes. It moves in
// small eased steps every twenty seconds rather than one long transition: a
// transition on the sun repaints the whole scene every frame for as long as
// it runs, which is what phones cannot afford. Each step updates the stamp,
// so a scene change re-applies the current height rather than the start.
const ARC_SECONDS = 600
const STEP_SECONDS = 20
const STEP = STEP_SECONDS / ARC_SECONDS
let driftTimer = null
let driftInterval = null
function stopDrift() {
  clearTimeout(driftTimer)
  clearInterval(driftInterval)
  driftInterval = null
}
function startDrift() {
  const root = document.documentElement
  const from = parseFloat(root.getAttribute('data-sun-alt'))
  // With transitions off the crawl would be a series of jumps, so it does not start.
  const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  if (isNaN(from) || reduce) return
  const target = from < 0.5 ? 1 : 0
  const dir = target > from ? 1 : -1
  root.style.setProperty('--sun-drift', '2.4s')
  root.style.setProperty('--sun-ease', 'ease-in-out')
  clearInterval(driftInterval)
  driftInterval = setInterval(() => {
    if (document.hidden) return
    const now = parseFloat(root.getAttribute('data-sun-alt'))
    if (isNaN(now)) return stopDrift()
    const next = dir > 0 ? Math.min(target, now + STEP) : Math.max(target, now - STEP)
    root.setAttribute('data-sun-alt', next.toFixed(3))
    root.style.setProperty('--sun-alt', next.toFixed(3))
    if (snapshot) snapshot.sunAlt = next
    if (next === target) stopDrift()
  }, STEP_SECONDS * 1000)
}

function start() {
  if (started || typeof window === 'undefined') return
  started = true
  refresh()
  // Transitions are enabled only now, so the first paint never crossfades.
  document.documentElement.setAttribute('data-live', '')
  // Let the entrance finish before the crawl begins.
  driftTimer = setTimeout(startDrift, 1800)
}

function subscribe(listener) {
  listeners.add(listener)
  start()
  return () => listeners.delete(listener)
}

const getSnapshot = () => snapshot
const getServerSnapshot = () => null

export function useSky() {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}

// A ?phase= or ?scene= query is for QA and would otherwise outrank the click.
function dropQuery(name) {
  try {
    const url = new URL(window.location.href)
    if (url.searchParams.has(name)) {
      url.searchParams.delete(name)
      window.history.replaceState(window.history.state, '', url)
    }
  } catch (e) { /* ignore */ }
}

// The sun steps the hour for this page load; nothing is saved.
export function stepPhase() {
  const root = document.documentElement
  const from = (snapshot && snapshot.phase) || root.getAttribute('data-phase') || 'golden'
  const next = skyFor(nextPhase(from))
  track('sun_click', { phase_from: from, phase_to: next.phase, scene: root.getAttribute('data-scene') || 'bay' })
  dropQuery('phase')
  stopDrift()
  // The step itself eases quickly; the crawl resumes from the new height.
  root.style.setProperty('--sun-drift', '900ms')
  root.style.setProperty('--sun-ease', 'ease')
  root.setAttribute('data-phase', next.phase)
  root.setAttribute('data-sun-alt', next.sunAlt.toFixed(3))
  root.setAttribute('data-rolled', '1')
  refresh()
  driftTimer = setTimeout(startDrift, 1000)
}

// The picker changes the scene for this page load only; nothing is saved.
export function setScene(name) {
  if (!SCENES.includes(name)) return
  const root = document.documentElement
  const from = root.getAttribute('data-scene') || 'bay'
  if (from !== name) track('scene_change', { scene_from: from, scene_to: name, phase: root.getAttribute('data-phase') || 'golden' })
  dropQuery('scene')
  root.setAttribute('data-scene', name)
  // A new landscape gets a new sun height within the current phase, eased
  // like a click; the crawl then resumes from there.
  const phase = (snapshot && snapshot.phase) || root.getAttribute('data-phase') || 'golden'
  const next = skyFor(phase)
  stopDrift()
  root.style.setProperty('--sun-drift', '900ms')
  root.style.setProperty('--sun-ease', 'ease')
  root.setAttribute('data-sun-alt', next.sunAlt.toFixed(3))
  root.setAttribute('data-rolled', '1')
  refresh()
  driftTimer = setTimeout(startDrift, 1000)
}
